import { CheckboxGroupOption } from "src/components/Inputs/CheckboxGroup";
import { ColumnId } from "../formTables/constants";

// See all column keys and labels: https://docs.google.com/spreadsheets/d/1JaWyN5IC12AytEFczdlSeQ5-xvTVC5nToM_IfrtEPj8/edit?usp=sharing

export type ColumnGroup = {
  groupName: string;
  options: CheckboxGroupOption[];
  columnWidthMin?: number;
  searchable?: boolean;
  searchLabel?: string;
  searchPlaceholder?: string;
};

export const getAllColumnGroups = ({
  isLotteryEnabled,
  isRankingEnabled,
  isCurrentApplyingSchoolsEnabled,
  orgPath,
  tagGroups,
  questionIdsAndLabelsInOrder,
  disclaimerIdsAndLabels,
}: {
  isLotteryEnabled: boolean;
  isRankingEnabled: boolean;
  orgPath: string | undefined;
  isCurrentApplyingSchoolsEnabled: boolean;
  tagGroups?: { id: uuid; label: string }[];
  questionIdsAndLabelsInOrder?: {
    id: uuid;
    label: string;
    isArchived: boolean;
  }[];
  disclaimerIdsAndLabels?: { id: uuid; label: string }[];
}): ColumnGroup[] => {
  return [
    {
      groupName: "Form information",
      options: getFormInfoOptions(isLotteryEnabled, isRankingEnabled, orgPath),
    },
    {
      groupName: "Tags",
      options: [
        { key: ColumnId.Tags, label: "Form tags" },
        ...(tagGroups ? getDynamicOptions(tagGroups, "tags") : []),
      ],
    },
    {
      groupName: "Student information",
      options: getStudentInfoOptions(isCurrentApplyingSchoolsEnabled),
    },
    {
      groupName: "Guardian information",
      options: guardianOptions,
    },
    {
      groupName: "Questions",
      options: [
        ...(questionIdsAndLabelsInOrder
          ? getDynamicOptions(questionIdsAndLabelsInOrder, "question")
          : []),
        ...(disclaimerIdsAndLabels
          ? getDynamicOptions(disclaimerIdsAndLabels, "disclaimer")
          : []),
      ],
      columnWidthMin: 500,
      searchable: true,
      searchLabel: "Search for a question",
      searchPlaceholder: "Type a question title",
    },
  ];
};

const LOTTERY_ONLY_TOOLTIP =
  "This value will be blank if Lottery & Offers is turned off on the form.";

const getFormInfoOptions = (
  isLotteryEnabled: boolean,
  isRankingEnabled: boolean,
  orgPath: string | undefined
): CheckboxGroupOption[] => {
  return [
    { key: ColumnId.ID, label: "ID" },
    { key: ColumnId.Status, label: "Status" },
    { key: ColumnId.StatusDate, label: "Status date" },
    { key: ColumnId.SubmittedDate, label: "Submitted date" },
    { key: ColumnId.SubStatus, label: "Sub-status" },
    { key: ColumnId.SubStatusDate, label: "Sub-status date" },
    { key: ColumnId.Errors, label: "Errors" },
    { key: ColumnId.ProgramId, label: "Program ID" },
    { key: ColumnId.Program, label: "Program" },
    {
      key: ColumnId.WaitlistPosition,
      label: "Waitlist position",
      ...(!isLotteryEnabled
        ? {
            tooltipContent: LOTTERY_ONLY_TOOLTIP,
          }
        : {}),
    },
    {
      key: ColumnId.LotteryPosition,
      label: "Lottery position",
      ...(!isLotteryEnabled
        ? {
            tooltipContent: LOTTERY_ONLY_TOOLTIP,
          }
        : {}),
    },
    {
      key: ColumnId.Tiebreaker,
      label: "Tiebreaker",
      ...(!isLotteryEnabled
        ? {
            tooltipContent: LOTTERY_ONLY_TOOLTIP,
          }
        : {}),
    },
    ...(orgPath === "nolaps" // This is hardcoded for NOLA, as they requested to have the form school rank id as a unique row identifier.
      ? [{ key: ColumnId.FormSchoolRowId, label: "Form school row ID" }]
      : []),
    ...(isRankingEnabled ? [{ key: ColumnId.SchoolRank, label: "Rank" }] : []),
    { key: ColumnId.EligibilityOverride, label: "Eligibility override" },
    { key: ColumnId.OverrideReason, label: "Override reason" },
    { key: ColumnId.SchoolId, label: "School ID" },
    { key: ColumnId.School, label: "School" },
    { key: ColumnId.SchoolReferenceId, label: "School reference ID" },
    { key: ColumnId.SchoolSubmittedDate, label: "School submitted date" },
    { key: ColumnId.Grade, label: "Grade" },
    { key: ColumnId.Verifications, label: "Verifications" },
  ];
};

const getStudentInfoOptions = (
  isCurrentApplyingSchoolsEnabled: boolean
): CheckboxGroupOption[] => {
  return [
    { key: ColumnId.StudentId, label: "Student ID" },
    { key: ColumnId.StudentFirstName, label: "Student first name" },
    { key: ColumnId.StudentMiddleName, label: "Student middle name" },
    { key: ColumnId.StudentLastName, label: "Student last name" },
    { key: ColumnId.StudentAddress, label: "Student address" },
    { key: ColumnId.StudentDateOfBirth, label: "Student birth date" },
    ...(isCurrentApplyingSchoolsEnabled
      ? [
          { key: ColumnId.AttendanceStatus, label: "Attendance status" },
          { key: ColumnId.AttendingSchool, label: "Attending school name" },
          { key: ColumnId.AttendingSchoolId, label: "Attending school ID" },
          {
            key: ColumnId.AttendingSchoolReferenceId,
            label: "Attending school reference ID",
          },
        ]
      : []),
  ];
};

const guardianOptions = [
  { key: ColumnId.GuardianId, label: "Guardian ID" },
  { key: ColumnId.GuardianFirstName, label: "Guardian first name" },
  { key: ColumnId.GuardianMiddleName, label: "Guardian middle name" },
  { key: ColumnId.GuardianLastName, label: "Guardian last name" },
  { key: ColumnId.GuardianAddress, label: "Guardian address" },
  { key: ColumnId.GuardianEmail, label: "Guardian email" },
  { key: ColumnId.GuardianPhone, label: "Guardian phone" },
  { key: ColumnId.GuardianLanguage, label: "Guardian preferred language" },
];

const getDynamicOptions = (
  options: { id: uuid; label: string; isArchived?: boolean }[],
  prefix: string
): CheckboxGroupOption[] => {
  return options
    .filter((option) => !option.isArchived)
    .map((option) => ({
      key: `${prefix}-${option.id}`,
      label: option.label,
    }));
};
